import { useCallback } from 'react';

import { createDealerWelcomeMessage } from '../../components/chat/utils';
import { useGameConfig } from '../../config/GameConfigProvider';
import { LIVE_SUPPORT_WELCOME_MESSAGE } from '../../utils/chat';
import { fetchTableLevelMessagesAction } from '../apiActions/fetchTableLevelMessagesAction';
import { useCoreDispatch } from '../CoreStore';
import { chatActions } from '../slices/chat/slice';
import { useConfigSlice } from '../slices/config/selectors';
import { useGameStateSlice } from '../slices/game/selectors';

export const useTableLevelMessagesFetcher = (essential: boolean): (() => void) => {
  const dispatch = useCoreDispatch();
  const chatUrl = useConfigSlice.env()?.chat;
  const tableId = useConfigSlice.tableId();
  const tableName = useConfigSlice.tableName() ?? '';
  const userId = useConfigSlice.userId();
  const showOtherMessages = useConfigSlice.showOtherMessages();
  const dealerName = useGameStateSlice.dealer()?.name ?? '';
  const hideDealerNameInWelcomeMessage = Boolean(useGameConfig().config?.chatConfig?.hideDealerName);

  const dealerWelcomeMessage = useCallback(
    () => createDealerWelcomeMessage(tableName, hideDealerNameInWelcomeMessage, dealerName),
    [dealerName, hideDealerNameInWelcomeMessage, tableName]
  );

  return useCallback(() => {
    if (chatUrl && tableId && userId) {
      dispatch(
        fetchTableLevelMessagesAction({
          url: `${chatUrl}/api/fetchTableLevelMessages`,
          offset: 0,
          recordsPerPage: 20,
          tableId: tableId,
          tagMessage: 'GAME_USER',
          userId: userId,
          showOtherMessages: showOtherMessages,
          essential,
        })
      )
        .unwrap()
        .then(() => {
          const dealerWelcomeMessageText = dealerWelcomeMessage();
          dispatch(chatActions.setDealerWelcomeMessage(dealerWelcomeMessageText));
          dispatch(chatActions.setInitialSupportMessage(LIVE_SUPPORT_WELCOME_MESSAGE));
          dispatch(chatActions.storeLastMessage(dealerWelcomeMessageText)); // Ensure welcome message is displayed in mobile chat ribbon
          dispatch(chatActions.storeRecentMessage(dealerWelcomeMessageText)); // Ensure welcome message is displayed in mini chat
        });
    }
  }, [chatUrl, tableId, userId, dispatch, showOtherMessages, essential, dealerWelcomeMessage]);
};
